import { normalizeText } from './format';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function numberOr(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function boolOr(value, fallback = false) {
  if (value === true || value === false) return value;
  if (value === 1 || value === '1' || String(value).toLowerCase() === 'true') return true;
  if (value === 0 || value === '0' || String(value).toLowerCase() === 'false') return false;
  return fallback;
}

export function productRestaurantConfig(product) {
  return product?.restaurant || product?.restaurante || product?.config_restaurant || product?.restaurant_config || {};
}

export function normalizeModifierOption(option, index = 0) {
  if (typeof option === 'string' || typeof option === 'number') {
    return {
      id: `option-${index + 1}`,
      nombre: String(option),
      cocina: String(option),
      precio: 0,
      default: false,
      activo: true,
      maxCantidad: 1,
      productoExtraId: null,
      metadata: null,
    };
  }

  const price = numberOr(
    option?.precio_adicional ?? option?.price_delta ?? option?.precio_extra ?? option?.recargo ?? option?.precio,
    0
  );
  const maxQty = Math.max(1, Math.round(numberOr(option?.max_cantidad ?? option?.max_qty ?? option?.maxQuantity, 1)));

  return {
    id: String(option?.id ?? option?.opcion_id ?? option?.key ?? `option-${index + 1}`),
    nombre: option?.nombre || option?.name || option?.label || `Opción ${index + 1}`,
    cocina: option?.etiqueta_cocina || option?.kitchen_label || option?.cocina || option?.nombre || option?.name || option?.label || '',
    precio: price,
    default: boolOr(option?.default ?? option?.predeterminado, false),
    activo: option?.activo !== false && option?.active !== false,
    maxCantidad: maxQty,
    productoExtraId: option?.producto_extra_id ?? option?.extra_product_id ?? null,
    metadata: option?.metadata || null,
  };
}

export function normalizeModifierGroup(group, index = 0) {
  const rawType = normalizeText(group?.tipo_seleccion || group?.selection_type || group?.tipo || group?.type || 'multiple');
  let tipo = 'multiple';
  if (['single', 'unica', 'unico', 'radio', 'one'].includes(rawType)) tipo = 'single';
  if (['quantity', 'cantidad', 'counter', 'contador'].includes(rawType)) tipo = 'quantity';

  const min = Math.max(0, Math.round(numberOr(group?.min_selecciones ?? group?.min ?? group?.minSelections, boolOr(group?.obligatorio ?? group?.required, false) ? 1 : 0)));
  const defaultMax = tipo === 'single' ? 1 : tipo === 'quantity' ? 99 : 99;
  const max = Math.max(min || 0, Math.round(numberOr(group?.max_selecciones ?? group?.max ?? group?.maxSelections, defaultMax)));
  const options = asArray(group?.opciones || group?.options || group?.items)
    .map(normalizeModifierOption)
    .filter((option) => option.activo);

  return {
    id: String(group?.id ?? group?.grupo_id ?? group?.key ?? `group-${index + 1}`),
    nombre: group?.nombre || group?.name || group?.label || `Opciones ${index + 1}`,
    descripcion: group?.descripcion || group?.description || '',
    tipo,
    min,
    max,
    obligatorio: boolOr(group?.obligatorio ?? group?.required, min > 0),
    orden: numberOr(group?.orden ?? group?.sort_order, index),
    options,
  };
}

export function getProductModifierGroups(product) {
  const config = productRestaurantConfig(product);
  const candidates = [
    config?.grupos_modificadores,
    config?.modifier_groups,
    product?.grupos_modificadores,
    product?.modifier_groups,
    product?.modificadores,
  ];
  let rows = candidates.find(Array.isArray) || [];

  // Compatibilidad con una configuración simple del producto. Gestión puede
  // evolucionar luego al modelo de grupos sin romper Tavolata.
  if (!rows.length) {
    const simple = [];
    const cooking = asArray(config?.cocciones || config?.tipos_coccion || product?.tipos_coccion);
    const removable = asArray(config?.ingredientes_removibles || product?.ingredientes_removibles);
    const extras = asArray(config?.extras || product?.extras);
    const sides = asArray(config?.acompanamientos || product?.acompanamientos);
    const sauces = asArray(config?.salsas || product?.salsas);

    if (cooking.length) simple.push({ id: 'coccion', nombre: 'Punto de cocción', tipo_seleccion: 'single', obligatorio: true, min: 1, max: 1, opciones: cooking });
    if (removable.length) simple.push({ id: 'quitar', nombre: 'Quitar ingredientes', tipo_seleccion: 'multiple', opciones: removable.map((row, i) => typeof row === 'string' ? { id: `sin-${i + 1}`, nombre: `Sin ${row}`, etiqueta_cocina: `SIN ${row}` } : row) });
    if (extras.length) simple.push({ id: 'extras', nombre: 'Extras', tipo_seleccion: 'quantity', opciones: extras });
    if (sides.length) simple.push({ id: 'acompanamiento', nombre: 'Acompañamiento', tipo_seleccion: 'single', obligatorio: Boolean(config?.acompanamiento_obligatorio), min: config?.acompanamiento_obligatorio ? 1 : 0, max: 1, opciones: sides });
    if (sauces.length) simple.push({ id: 'salsas', nombre: 'Salsas', tipo_seleccion: 'multiple', opciones: sauces });
    rows = simple;
  }

  return rows
    .map(normalizeModifierGroup)
    .filter((group) => group.options.length)
    .sort((a, b) => a.orden - b.orden);
}

export function isRestaurantFood(product) {
  const config = productRestaurantConfig(product);
  return boolOr(
    config?.es_comestible ?? config?.is_food ?? product?.es_comestible ?? product?.comestible,
    false
  );
}

export function allowsFreeNote(product) {
  const config = productRestaurantConfig(product);
  const explicit = config?.permite_observacion ?? config?.allow_note ?? product?.permite_observacion;
  if (explicit === undefined || explicit === null) return true;
  return boolOr(explicit, true);
}

export function modifierSelectionKey(selection) {
  return `${selection.groupId}:${selection.optionId}:${Number(selection.quantity || 1)}`;
}

export function modifiersSignature(selections = [], note = '', critical = false) {
  const list = asArray(selections)
    .map(modifierSelectionKey)
    .sort()
    .join('|');
  return `${list}::${normalizeText(note)}::${critical ? '1' : '0'}`;
}

export function unitModifierTotal(selections = []) {
  return asArray(selections).reduce((sum, selection) => (
    sum + numberOr(selection?.price, 0) * Math.max(1, numberOr(selection?.quantity, 1))
  ), 0);
}

export function itemUnitPrice(item) {
  return numberOr(item?.producto?.precio_bruto ?? item?.precio_bruto, 0) + unitModifierTotal(item?.modifierSelections);
}

export function itemLineTotal(item) {
  return itemUnitPrice(item) * Math.max(0, numberOr(item?.cantidad, 0));
}

export function selectionsToKitchenLines(selections = []) {
  return asArray(selections)
    .filter((selection) => selection?.optionName)
    .map((selection) => {
      const qty = Math.max(1, numberOr(selection?.quantity, 1));
      const label = selection?.kitchenLabel || selection?.optionName;
      return qty > 1 ? `${label} ×${qty}` : label;
    });
}

export function buildDefaultSelections(groups = []) {
  const selections = [];
  groups.forEach((group) => {
    group.options.filter((option) => option.default).forEach((option) => {
      selections.push({
        groupId: group.id,
        groupName: group.nombre,
        optionId: option.id,
        optionName: option.nombre,
        kitchenLabel: option.cocina,
        price: option.precio,
        quantity: 1,
        productoExtraId: option.productoExtraId,
      });
    });
  });
  return selections;
}

export function validateModifierSelections(groups = [], selections = []) {
  for (const group of groups) {
    const selected = selections.filter((selection) => selection.groupId === group.id);
    const count = group.tipo === 'quantity'
      ? selected.reduce((sum, selection) => sum + Math.max(1, numberOr(selection.quantity, 1)), 0)
      : selected.length;
    if (count < group.min) return `Seleccione al menos ${group.min} opción(es) en “${group.nombre}”.`;
    if (group.max > 0 && count > group.max) return `Puede seleccionar hasta ${group.max} opción(es) en “${group.nombre}”.`;
  }
  return '';
}
